import React from 'react';
import { mapSvgAttributes } from '../utils/svgAttrMap';
import { SVG_SHAPES } from '../utils/svgUtils';

// Spinner component
function Spinner() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
      <div style={{
        border: '4px solid #e0e0e0',
        borderTop: '4px solid #2563eb',
        borderRadius: '50%',
        width: 48,
        height: 48,
        animation: 'spin 1s linear infinite',
        marginBottom: 16
      }} />
      <span style={{ color: '#2563eb', fontWeight: 500, fontSize: 18 }}>Loading...</span>
      <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ErrorBoundary component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true };
  }
  componentDidCatch(error, errorInfo) {}
  render() {
    if (this.state.hasError) {
      return <div className="error-message">An error occurred while rendering the SVG. Displaying as a static image.</div>;
    }
    return this.props.children;
  }
}

// Utility to parse style strings to React style objects
function parseStyleString(styleString) {
  if (!styleString) return {};
  return styleString.split(';').reduce((acc, rule) => {
    const [key, value] = rule.split(':').map(s => s && s.trim());
    if (key && value) acc[key.replace(/-([a-z])/g, g => g[1].toUpperCase())] = value;
    return acc;
  }, {});
}

function renderSvgNode(node, { hoveredId, setHoveredId, selectedId, setSelectedId, setSelectedElement, pathIndex = 0, isRoot = false }) {
  if (!node || !node.name) return null;
  const { name, attributes, children, value } = node;
  const isShape = SVG_SHAPES.includes(name);
  const id = attributes['data-svgtoolbox-id'] || attributes.id || `${name}-${pathIndex}`;
  const styleProp = attributes.style
    ? (typeof attributes.style === 'string'
        ? parseStyleString(attributes.style)
        : attributes.style)
    : undefined;
  const colorAttr = attributes.fill || attributes.stroke;
  const isSelectedShape = selectedId === id && isShape;
  const outline = (!isSelectedShape && hoveredId === id && isShape)
    ? { stroke: '#007bff', strokeWidth: 3, filter: 'drop-shadow(0 0 2px #007bff)' }
    : {};
  const elementProps = {
    key: id,
    ...mapSvgAttributes(attributes),
    ...(styleProp ? { style: styleProp } : {}),
    ...(isShape ? { ...outline } : {}),
    ...(isShape ? {
      onMouseOver: () => setHoveredId(id),
      onMouseOut: () => setHoveredId(null),
      onClick: (e) => {
        e.stopPropagation();
        console.log('Element clicked:', { id, name, attributes });
        setSelectedId(id);
        setSelectedElement({ id, name, color: colorAttr, attributes });
      }
    } : {}),
    ...(isSelectedShape ? { className: (attributes.className ? attributes.className + ' marching-ants' : 'marching-ants') } : {})
  };
  // Suppress original stroke/strokeWidth for marching ants, but preserve in data-original-* attributes
  if (isSelectedShape) {
    if ('stroke' in elementProps) elementProps['data-original-stroke'] = elementProps.stroke;
    if ('strokeWidth' in elementProps) elementProps['data-original-stroke-width'] = elementProps.strokeWidth;
    delete elementProps.stroke;
    delete elementProps.strokeWidth;
    delete elementProps.filter;
  }

  // Special handling for text elements
  if (name === 'text' && value) {
    return React.createElement(
      name,
      elementProps,
      value // Include the text content
    );
  }

  return React.createElement(
    name,
    elementProps,
    children && children.map((child, i) =>
      renderSvgNode(child, { hoveredId, setHoveredId, selectedId, setSelectedId, setSelectedElement, pathIndex: i, isRoot: false })
    )
  );
}

export default function SvgPreview({ previewSvg, svgJson, isComplex, hoveredId, setHoveredId, selectedId, setSelectedId, setSelectedElement, error, loading }) {
  return (
    <div className="preview-container">
      {loading ? (
        <Spinner />
      ) : error ? (
        <div className="error-message">{error}</div>
      ) : previewSvg ? (
        isComplex ? (
          <div className="svg-preview">
            <div className="error-message">This SVG is too complex for interactive editing. Displaying as a static image.</div>
            <div dangerouslySetInnerHTML={{ __html: previewSvg }} />
          </div>
        ) : (
          <ErrorBoundary>
            <div className="svg-preview">
              {svgJson ? (() => {
                try {
                  return renderSvgNode(svgJson, { hoveredId, setHoveredId, selectedId, setSelectedId, setSelectedElement, isRoot: true });
                } catch (err) {
                  return <div>
                    <div className="error-message">This SVG could not be rendered interactively. Displaying as a static image.</div>
                    <div dangerouslySetInnerHTML={{ __html: previewSvg }} />
                  </div>;
                }
              })() : null}
            </div>
          </ErrorBoundary>
        )
      ) : (
        <div style={{ 
          textAlign: 'center', 
          color: '#64748b',
          fontSize: '1.1rem',
          lineHeight: 1.6,
          maxWidth: '600px',
          margin: '0 auto'
        }}>
          <p style={{ marginBottom: '1rem' }}>
            Vector Tuner is a lightweight SVG editor designed for quick adjustments to existing vector graphics. Whether you've received an SVG file from a colleague or generated one with AI, Vector Tuner lets you make simple modifications without needing to learn complex design software.
          </p>
          <p style={{ marginBottom: '1rem' }}>
            Perfect for anyone who needs to tweak colors, adjust shapes, or fine-tune attributes in their SVG files - no design experience required.
          </p>
          <p>
            Upload an SVG file or open one from Google Drive to get started
          </p>
        </div>
      )}
    </div>
  );
} 